import React from 'react';
import Avatar from '../../../../components/shared/Avatar/Avatar';
import './ShareComposer.css';

import { MAX_POST_LENGTH } from '../../../../services/posts/api';

interface ShareComposerProps {
  userAvatar?: string;
  userName?: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  /** Photo attachments (T2.10b) — up to maxAttachments images per post. */
  attachments?: File[];
  onAttachmentsChange?: (files: File[]) => void;
  maxAttachments?: number;
}

const ShareComposer: React.FC<ShareComposerProps> = ({
  userAvatar,
  userName = 'You',
  value,
  onChange,
  onSubmit,
  disabled = false,
  attachments = [],
  onAttachmentsChange,
  maxAttachments = 5,
}) => {
  const canSubmit = value.trim().length > 0 && !disabled;
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  // Object URLs for previews — one per attachment, revoked on change/unmount.
  const previewUrls = React.useMemo(
    () => attachments.map((file) => URL.createObjectURL(file)),
    [attachments],
  );
  React.useEffect(() => () => {
    previewUrls.forEach((url) => URL.revokeObjectURL(url));
  }, [previewUrls]);

  const handleFilesPicked = (fileList: FileList | null) => {
    if (!fileList || !onAttachmentsChange) return;
    const picked = Array.from(fileList).filter((file) => file.type.startsWith('image/'));
    onAttachmentsChange([...attachments, ...picked].slice(0, maxAttachments));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index: number) => {
    if (!onAttachmentsChange) return;
    onAttachmentsChange(attachments.filter((_, i) => i !== index));
  };

  return (
    <section className="v2-share-composer" aria-label="Share your adventure">
      <h2 className="v2-share-composer__title">Share Your Last Adventure</h2>
      <div className="v2-share-composer__card">
        <div className="v2-share-composer__input-row">
          <Avatar
            src={userAvatar}
            alt={userName}
            size="medium"
            className="v2-share-composer__avatar"
          />
          <textarea
            className="v2-share-composer__textarea"
            maxLength={MAX_POST_LENGTH}
            placeholder="Share your last adventure..."
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={1}
            aria-label="Adventure description"
          />
        </div>
        {attachments.length > 0 && (
          <div className="v2-share-composer__attachments" aria-label="Photos to attach">
            {previewUrls.map((url, index) => (
              <div key={url} className="v2-share-composer__attachment">
                <img src={url} alt={`Attachment ${index + 1}`} />
                <button
                  type="button"
                  onClick={() => removeAttachment(index)}
                  aria-label={`Remove photo ${index + 1}`}
                >
                  ×
                </button>
              </div>
            ))}
            <span className="v2-share-composer__attachment-count">
              {attachments.length}/{maxAttachments}
            </span>
          </div>
        )}
        <div className="v2-share-composer__footer">
          <div className="v2-share-composer__tools">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(event) => handleFilesPicked(event.target.files)}
            />
            <button
              type="button"
              className="v2-share-composer__tool"
              title="Add photo"
              aria-label="Add photo"
              disabled={disabled || attachments.length >= maxAttachments}
              onClick={() => fileInputRef.current?.click()}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button type="button" className="v2-share-composer__tool" title="Add location" aria-label="Add location">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" />
                <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
            <button type="button" className="v2-share-composer__tool" title="Add route" aria-label="Add route">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle cx="6" cy="6" r="2" stroke="currentColor" strokeWidth="2" />
                <circle cx="18" cy="18" r="2" stroke="currentColor" strokeWidth="2" />
                <path d="M8 6h5a5 5 0 0 1 5 5v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <button
            type="button"
            className="v2-share-composer__submit"
            onClick={onSubmit}
            disabled={!canSubmit}
          >
            Post
          </button>
        </div>
      </div>
    </section>
  );
};

export default ShareComposer;
